import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  OnDestroy,
  output,
  signal,
} from "@angular/core";
import { unByKey } from "ol/Observable";
import { EventsKey } from "ol/events";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { NgSelectModule } from "@ng-select/ng-select";
import { TranslateModule } from "@ngx-translate/core";
import { BehaviorSubject, forkJoin, Subject, takeUntil } from "rxjs";
import proj4 from "proj4";
import { register } from "ol/proj/proj4";
import { transform } from "ol/proj";
import { Graticule, Map, View } from "ol";
import { Stroke } from "ol/style";
import Fill from "ol/style/Fill";
import OLText from "ol/style/Text";

import { UtilisateurService } from "src/app/services/utilisateur.service";
import { HttpService } from "src/app/services/http.service";
import { LoggerService } from "src/app/services/logger.service";
import {
  Blast,
  HoleService,
  HoleStatus,
  LoadingHoleTypeQuality,
} from "src/app/services/hole.service";
import { Site } from "src/app/bo/site";
import { SiteHoleTolerance, getThreshold } from "src/app/bo/site-hole-tolerance";
import { TirsPolygoneEnum } from "src/app/bo/polygon/tirPolygoneEnum";
import MapConstants from "../../carte/constants";
import { BlastPolygonsLayer } from "../../carte/layers/BlastPolygoneLayer";
import { HoleLayer } from "../../carte/layers/holeLayer";
import { CarteHelpers } from "../../carte/carteHelpers";

export enum FilterType {
  State = "STATE",
  Quality = "QUALITY",
  Dates = "DATES",
  Delivery = "DELIVERY",
}

export enum QualityTypeColor {
  Good = "GOOD",
  Average = "AVERAGE",
  Bad = "BAD",
}

interface FilterOption {
  id: FilterType;
  nom: string;
}

interface QualityOption {
  key: LoadingHoleTypeQuality;
  label: string;
  unit: string;
}

interface QualityColorOption {
  color: string;
  type: QualityTypeColor;
}

interface HoleData {
  holeName: string;
  totalBulk: number;
  steaming: number;
  linearQt: number;
  date: string;
}

interface DeliveryData {
  holeName: string;
  deliveryDate: Date;
  operatorName: string;
  deliveryId?: string;
}

@Component({
  selector: "app-blast-load-data",
  templateUrl: "./blast-load-data.component.html",
  styleUrls: ["./blast-load-data.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, TranslateModule, NgSelectModule, FormsModule],
})
export class BlastLoadDataComponent implements OnDestroy {
  // Inputs/Outputs
  readonly blast = input<Blast | null>(null);
  readonly qualityData = input<SiteHoleTolerance | null>(null);
  readonly selectedHoleId = input<string | null>(null);
  readonly holeSelected = output<{ holeId: string | null; changeCollapsed: boolean }>();

  // Map related properties
  site: Site;
  map: Map;
  view = new View();
  firstHolePosition: [number, number] = [0, 0];
  private blastPolygoneLayer: BlastPolygonsLayer;
  protected holeLayer: HoleLayer;
  graticule: Graticule;
  readonly groups: (HoleStatus | "actual")[][] = [];

  // Filter and display state — signals
  protected readonly selectedHoleStatusFilter = signal<string | HoleStatus>("actual");
  protected readonly holesAreShown = signal(false);
  protected readonly listeTirsSelectionnes = signal<number[]>([]);
  readonly listeTypesSelectionnes: number[];

  // Tab state — signals
  protected readonly topTab = signal<"load" | "dashboard">("load");
  protected readonly viewTab = signal<"2d" | "3d">("2d");

  // Filter configuration — signals
  public FilterType = FilterType;
  protected readonly selectedType = signal<FilterType>(FilterType.State);
  protected readonly qualitySelected = signal("BULK");
  protected readonly dateSelected = signal<string[]>([]);
  protected readonly deliverySelected = signal<string[]>([]);
  protected readonly dates = signal<string[]>([]);
  protected readonly holeDataList = signal<HoleData[]>([]);
  protected readonly listDeliveries = signal<DeliveryData[]>([]);

  protected readonly uniqueDeliveries = computed(() => {
    const unique: DeliveryData[] = [];
    const seenKeys = new Set<string>();
    this.listDeliveries().forEach(delivery => {
      const key = `${delivery.operatorName}_${delivery.deliveryDate.toISOString()}`;
      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        unique.push(delivery);
      }
    });
    return unique;
  });

  protected readonly areAllSelected = computed(() => {
    switch (this.selectedType()) {
      case FilterType.Delivery: {
        const ud = this.uniqueDeliveries();
        if (ud.length === 0) return false;
        return ud.every(d => this.deliverySelected().includes(`${d.operatorName}_${d.deliveryDate.toISOString()}`));
      }
      case FilterType.Dates:
        if (this.dates().length === 0) return false;
        return this.dates().every(date => this.dateSelected().includes(date));
      default:
        return false;
    }
  });

  protected readonly areSomeSelected = computed(() => {
    switch (this.selectedType()) {
      case FilterType.Delivery:
        return this.deliverySelected().length > 0;
      case FilterType.Dates:
        return this.dateSelected().length > 0;
      default:
        return false;
    }
  });

  // Static data arrays
  readonly qualities: QualityOption[] = [
    { key: LoadingHoleTypeQuality.Bulk, label: "BULK_EXPLOSIVES", unit: "kg" },
    { key: LoadingHoleTypeQuality.StreamingFinal, label: "STEMMING", unit: "m" },
    { key: LoadingHoleTypeQuality.LinearQuantity, label: "LINEAR_QT", unit: "kg/m" },
  ];

  readonly qualitiesColor: QualityColorOption[] = [
    { color: "green", type: QualityTypeColor.Good },
    { color: "yellow", type: QualityTypeColor.Average },
    { color: "red", type: QualityTypeColor.Bad },
  ];

  readonly types: FilterOption[] = [
    { id: FilterType.State, nom: "STATE" },
    { id: FilterType.Quality, nom: "QUALITY" },
    { id: FilterType.Dates, nom: "DATES" },
    { id: FilterType.Delivery, nom: "DELIVERY" },
  ];

  // Internal state
  private readonly unsubscribe$ = new Subject<void>();
  private proj4Site = "";
  private refreshHolesTimeout: any;
  private resolutionListenerKey: EventsKey | null = null;
  private readonly LABEL_HIDE_RESOLUTION = 0.1;

  protected readonly cartSelectedTirsPolygoneEnum: TirsPolygoneEnum[] = [
    TirsPolygoneEnum.DRILLED,
  ];

  readonly isMapLoading$ = new BehaviorSubject<boolean>(false);
  protected readonly isMapLoading = toSignal(this.isMapLoading$, { initialValue: false });

  constructor(
    protected userService: UtilisateurService,
    private readonly http: HttpService,
    private readonly holeService: HoleService,
    private readonly logger: LoggerService
  ) {
    this.listeTypesSelectionnes = this.cartSelectedTirsPolygoneEnum.map((x) =>
      CarteHelpers.enumToNumber(x)
    );

    this.holeStateLoadGroups();

    effect(() => {
      const b = this.blast();
      if (!b?.BlastLoading?.length) return;
      this.isMapLoading$.next(true);
      this.selectedHoleStatusFilter.set("actual");
      this.holeDataList.set(this.createHoleDataList(b));
      const deliveries = this.createDeliveryList(b);
      this.listDeliveries.set(deliveries);

      const d = this.getDates(b);
      this.dates.set(d);
      this.dateSelected.set([...d]);

      const ud = this.computeUniqueDeliveries(deliveries);
      this.deliverySelected.set(ud.map(del => `${del.operatorName}_${del.deliveryDate.toISOString()}`));

      this.firstHolePosition = [b.BlastLoading[0].holePosition.x, b.BlastLoading[0].holePosition.y];
      this.listeTirsSelectionnes.set([b.tirId]);

      if (this.map && this.site) {
        this.repositionMapAndRefresh();
      } else {
        this.initMap();
      }
    });

    effect(() => {
      const holeId = this.selectedHoleId();
      if (this.holeLayer && this.blast()?.BlastLoading) {
        this.selectHoleOnMap(holeId);
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();

    if (this.refreshHolesTimeout) {
      clearTimeout(this.refreshHolesTimeout);
    }

    if (this.resolutionListenerKey) {
      unByKey(this.resolutionListenerKey);
      this.resolutionListenerKey = null;
    }

    if (this.map) {
      this.map.setTarget(null);
    }
  }

  private initMap() {
    this.site = this.userService.currentSite();
    this.onChangeSite();
  }

  private repositionMapAndRefresh() {
    if (!this.map || !this.site || !this.firstHolePosition) {
      return;
    }

    if (this.refreshHolesTimeout) {
      clearTimeout(this.refreshHolesTimeout);
    }

    this.clearMapLayers();

    const coordTrous = transform(
      [this.firstHolePosition[0], this.firstHolePosition[1]],
      "EPSG:" + this.site.epsg,
      MapConstants.EPSG_REFERENCE_OL
    );

    this.view.setCenter(coordTrous);
    this.view.setZoom(20);

    requestAnimationFrame(() => {
      this.holesAreShown.set(true);

      this.blastPolygoneLayer = new BlastPolygonsLayer(
        this.map,
        this.site.siteId,
        this.http,
        20
      );

      this.holeLayer = new HoleLayer(
        this.map,
        this.holeService,
        this.logger,
        30
      );
      this.holeLayer.setShowHoleNames(true);
      this.configureZoomForLabels();

      this.DisplayBlastPolygons(this.listeTirsSelectionnes());

      requestAnimationFrame(() => {
        this.RefreshHoles();
      });
    });
  }

  onChangeSite() {
    forkJoin([this.http.get<string>("Sites/proj4/" + this.site.epsg)])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((resultat) => {
        this.proj4Site = resultat[0];
        this.registerProjString(this.proj4Site);

        this.createMap();

        this.blastPolygoneLayer = new BlastPolygonsLayer(
          this.map,
          this.site.siteId,
          this.http,
          20
        );

        this.holeLayer = new HoleLayer(
          this.map,
          this.holeService,
          this.logger,
          30
        );

        this.holeLayer.setShowHoleNames(true);

        this.onClick();

        this.map.addLayer(this.createGraticule());
        this.addWhiteSquare();

        this.configureOnPan();
        this.configureZoomForLabels();
        this.holesAreShown.set(true);
        this.DisplayBlastPolygons(this.listeTirsSelectionnes());

        setTimeout(() => {
          this.RefreshHoles();
          setTimeout(() => {
            this.isMapLoading$.next(false);
          }, 300);
        }, 500);
      });
  }

  private createMap() {
    const coordTrous = transform(
      [this.firstHolePosition[0], this.firstHolePosition[1]],
      "EPSG:" + this.site.epsg,
      MapConstants.EPSG_REFERENCE_OL
    );

    if (this.view) {
      this.view.setCenter(coordTrous);
      this.view.setZoom(20);
    } else {
      this.view = new View({
        center: coordTrous,
        zoom: 20,
      });
    }

    if (this.map) {
      this.map.setView(this.view);
    } else {
      this.map = new Map({
        layers: null,
        target: "map-load",
        view: this.view,
        controls: [],
      });
    }
  }

  private configureOnPan() {
    this.map.on("moveend", () => {
      if (this.refreshHolesTimeout) {
        clearTimeout(this.refreshHolesTimeout);
      }
      this.refreshHolesTimeout = setTimeout(() => {
        this.RefreshHoles();
      }, 100);
    });
  }

  private configureZoomForLabels(): void {
    if (this.resolutionListenerKey) {
      unByKey(this.resolutionListenerKey);
    }
    this.resolutionListenerKey = this.map.getView().on('change:resolution', () => {
      if (!this.holeLayer) return;
      const resolution = this.map.getView().getResolution();
      this.holeLayer.setShowHoleNames(resolution <= this.LABEL_HIDE_RESOLUTION);
    }) as EventsKey;

    // Apply immediately so labels reflect the current zoom level
    if (this.holeLayer) {
      const resolution = this.map.getView().getResolution();
      this.holeLayer.setShowHoleNames(resolution <= this.LABEL_HIDE_RESOLUTION);
    }
  }

  private createGraticule(): Graticule {
    this.graticule = new Graticule({
      targetSize: 60,
      showLabels: true,
      strokeStyle: new Stroke({ color: "rgba(126, 124, 124, 0.36)", width: 1 }),
      lonLabelFormatter: (lon) => this.formatLonLabel(lon),
      latLabelFormatter: (lat) => this.formatLatLabel(lat),
      lonLabelPosition: 0.15,
      latLabelPosition: 0.055,
      lonLabelStyle: new OLText({
        font: "12px Roboto",
        fill: new Fill({ color: "#5A5A5A" }),
      }),
      latLabelStyle: new OLText({
        font: "12px Roboto",
        fill: new Fill({ color: "#5A5A5A" }),
      }),
    });

    return this.graticule;
  }

  private formatLonLabel(lon: number): string {
    const viewCenter = this.map.getView().getCenter();
    const viewCenterInWGS84 = transform(
      viewCenter,
      this.map.getView().getProjection(),
      "EPSG:4326"
    );
    const pointInSiteProjection = transform(
      [lon, viewCenterInWGS84[1]],
      "EPSG:4326",
      "EPSG:" + this.site.epsg
    );

    if (this.isLabelInWhiteSquareArea(lon, true)) {
      return "";
    }

    return `${pointInSiteProjection[0].toFixed(0)}`;
  }

  private formatLatLabel(lat: number): string {
    const viewCenter = this.map.getView().getCenter();
    const viewCenterInWGS84 = transform(
      viewCenter,
      this.map.getView().getProjection(),
      "EPSG:4326"
    );
    const pointInSiteProjection = transform(
      [viewCenterInWGS84[0], lat],
      "EPSG:4326",
      "EPSG:" + this.site.epsg
    );

    if (this.isLabelInWhiteSquareArea(lat, false)) {
      return "";
    }

    return `${pointInSiteProjection[1].toFixed(0)}`;
  }

  private registerProjString(projString: string) {
    if (undefined !== projString && null !== projString && projString != "") {
      proj4.defs("EPSG:" + this.site.epsg, projString);
      register(proj4);
    }
  }

  private DisplayBlastPolygons(blastIdToDisplay?: number[]) {
    this.blastPolygoneLayer
      .displayPolygonsForBlast(
        blastIdToDisplay,
        this.listeTypesSelectionnes,
        false,
        4
      )
      .finally(() => {});
  }

  private RefreshHoles(): void {
    const viewExtent = this.map.getView().calculateExtent(this.map.getSize());
    const visibleBlasts = this.blastPolygoneLayer.findPolygoneInView(viewExtent);

    let blastIdsInView = this.getUniqueBlastIds(visibleBlasts);

    if (this.listeTirsSelectionnes().length > 0) {
      for (const selectedBlastId of this.listeTirsSelectionnes()) {
        if (!blastIdsInView.includes(selectedBlastId)) {
          blastIdsInView.push(selectedBlastId);
        }
      }
    }

    this.updateQualityFilter();

    this.holeLayer.refreshHoles(
      blastIdsInView,
      this.holesAreShown(),
      this.listeTirsSelectionnes(),
      this.getVisibleHoleStatuses(),
      this.selectedHoleStatusFilter() === "actual"
        ? ("actual" as any)
        : (this.selectedHoleStatusFilter() as HoleStatus),
      null,
      this.blast(),
      this.isMapLoading$,
      false,
      true
    );
  }

  private getUniqueBlastIds(visibleBlasts: any[]): number[] {
    const blastIds: number[] = [];
    for (const feature of visibleBlasts) {
      const blastId = feature.get(MapConstants.TIR_ID).value;
      if (!blastIds.includes(blastId)) {
        blastIds.push(blastId);
      }
    }
    return blastIds;
  }

  protected updateHoleVisibility() {
    if (this.holesAreShown()) {
      const visibleStatuses = this.getVisibleHoleStatuses();
      this.updateQualityFilter();

      this.holeLayer.refreshHoleVisibility(
        visibleStatuses,
        this.selectedHoleStatusFilter() === "actual"
          ? ("actual" as any)
          : (this.selectedHoleStatusFilter() as HoleStatus)
      );
    } else {
      this.holeLayer.hideAllHoles();
    }
  }

  private getVisibleHoleStatuses(): Set<HoleStatus> {
    if (this.selectedHoleStatusFilter() === "actual") {
      return new Set(this.getHoleStatusLoad());
    } else {
      return new Set([this.selectedHoleStatusFilter() as HoleStatus]);
    }
  }

  protected onHoleStatusFilterChange(filter: string | HoleStatus) {
    this.selectedHoleStatusFilter.set(filter);
    this.updateHoleVisibility();
  }

  protected holeStateLoadGroups() {
    const statuses = this.getHoleStatusLoad();
    const withActual: Array<HoleStatus | "actual"> = ["actual", ...statuses];

    for (let i = 0; i < withActual.length; i += 3) {
      this.groups.push(withActual.slice(i, i + 3));
    }
  }

  private getHoleStatusLoad(): HoleStatus[] {
    const includedStatuses = new Set([
      HoleStatus.PLANNED,
      HoleStatus.MISSING,
      HoleStatus.COLLAPSED,
      HoleStatus.DRILLED,
      HoleStatus.LOADING,
      HoleStatus.LOADED,
    ]);

    return Object.values(HoleStatus).filter(
      (status) => includedStatuses.has(status)
    );
  }

  protected getHoleStatusIcon(status: HoleStatus | "actual"): string {
    if (status === "actual") {
      return "assets/icon/all_holes.svg";
    }
    return this.holeLayer.getHoleIconPath(status as string);
  }

  private addWhiteSquare(): void {
    setTimeout(() => {
      const mapContainer = document.getElementById("map-load");
      if (mapContainer) {
        const whiteSquare = document.createElement("div");
        whiteSquare.style.position = "absolute";
        whiteSquare.style.bottom = "0px";
        whiteSquare.style.left = "0px";
        whiteSquare.style.width = "50px";
        whiteSquare.style.height = "50px";
        whiteSquare.style.backgroundColor = "white";
        whiteSquare.style.zIndex = "1000";
        whiteSquare.style.pointerEvents = "none";

        mapContainer.appendChild(whiteSquare);
      }
    }, 100);
  }

  private isLabelInWhiteSquareArea(
    coordinateValue: number,
    isLongitude: boolean
  ): boolean {
    if (!this.map) return false;

    const mapSize = this.map.getSize();
    if (!mapSize) return false;

    const view = this.map.getView();
    const extent = view.calculateExtent(mapSize);

    const whiteSquareWidthPixels = 50;
    const whiteSquareHeightPixels = 50;

    const resolution = view.getResolution();
    if (!resolution) return false;

    const whiteSquareWidthUnits = whiteSquareWidthPixels * resolution;
    const whiteSquareHeightUnits = whiteSquareHeightPixels * resolution;

    if (isLongitude) {
      const leftBoundary = extent[0];
      const whiteSquareRightBoundary = leftBoundary + whiteSquareWidthUnits;

      const viewCenter = this.map.getView().getCenter();
      if (!viewCenter) return false;

      const viewCenterInWGS84 = transform(viewCenter, view.getProjection(), "EPSG:4326");
      const pointInViewProjection = transform(
        [coordinateValue, viewCenterInWGS84[1]],
        "EPSG:4326",
        view.getProjection()
      );

      return pointInViewProjection[0] <= whiteSquareRightBoundary;
    } else {
      const bottomBoundary = extent[1];
      const whiteSquareTopBoundary = bottomBoundary + whiteSquareHeightUnits;

      const viewCenter = this.map.getView().getCenter();
      if (!viewCenter) return false;

      const viewCenterInWGS84 = transform(viewCenter, view.getProjection(), "EPSG:4326");
      const pointInViewProjection = transform(
        [viewCenterInWGS84[0], coordinateValue],
        "EPSG:4326",
        view.getProjection()
      );

      return pointInViewProjection[1] <= whiteSquareTopBoundary;
    }
  }

  private onClick(): void {
    this.map.on("click", (evt) => {
      const features = this.map.getFeaturesAtPixel(evt.pixel);

      if (!features || features.length === 0) {
        return;
      }

      const holeFeature = features.find(
        (feature) =>
          feature.get(MapConstants.FEATURE_NAME) ===
          MapConstants.FEATURE_TYPE.hole
      );

      if (holeFeature) {
        const hole = holeFeature.get(MapConstants.HOLE_OBJECT);
        if (hole) {
          this.selectHole(hole);
        }
      }
    });
  }

  private selectHole(hole: any): void {
    this.holeLayer.setSelectedHole(hole.name);
    this.holeLayer.refreshHoleStyles();
    this.holeSelected.emit({ holeId: hole.publicGuid, changeCollapsed: false });
  }

  private selectHoleOnMap(holeId: string | null): void {
    if (!this.holeLayer || !this.blast()?.BlastLoading) {
      return;
    }

    if (!holeId) {
      this.holeLayer.setSelectedHole(null);
      this.holeLayer.refreshHoleStyles();
      return;
    }

    const hole = this.blast().BlastLoading.find(h => h.holeId === holeId);
    if (hole) {
      this.holeLayer.setSelectedHole(hole.name);
      this.holeLayer.refreshHoleStyles();
      this.centerMapOnHole(hole);
    }
  }

  private centerMapOnHole(hole: any): void {
    if (!this.map || !this.site || !hole.holePosition) {
      return;
    }

    const coordTrous = transform(
      [hole.holePosition.x, hole.holePosition.y],
      "EPSG:" + this.site.epsg,
      MapConstants.EPSG_REFERENCE_OL
    );

    this.view.animate({
      center: coordTrous,
      duration: 500
    });
  }

  onTypeChange(value: FilterType | null) {
    this.selectedType.set(value);
    this.updateQualityFilter();
    this.RefreshHoles();
  }

  onQualitySelectedChange(value: string) {
    this.qualitySelected.set(value);
    if (this.selectedType() === FilterType.Quality) {
      this.RefreshHoles();
    }
  }

  private updateQualityFilter() {
    if (this.selectedType() === FilterType.Quality && this.holeLayer) {
      const qualityFilterInfo = {
        selectedMetric: this.qualitySelected(),
        holeDataList: this.holeDataList(),
        tolerance: this.qualityData(),
      };
      this.holeLayer.setLoadingQualityFilter(qualityFilterInfo);
      this.holeLayer.setLoadingDateFilter(null);
      this.holeLayer.setLoadingDeliveryFilter(null);
    } else if (this.selectedType() === FilterType.Dates && this.holeLayer) {
      const dateFilterInfo = {
        selectedDates: this.dateSelected(),
        holeDataList: this.holeDataList(),
        allDates: this.dates(),
      };
      this.holeLayer.setLoadingDateFilter(dateFilterInfo);
      this.holeLayer.setLoadingQualityFilter(null);
      this.holeLayer.setLoadingDeliveryFilter(null);
    } else if (this.selectedType() === FilterType.Delivery && this.holeLayer) {
      const deliveryFilterInfo = {
        selectedDeliveries: this.deliverySelected(),
        deliveryDataList: this.listDeliveries(),
        allDeliveries: this.listDeliveries(),
      };
      this.holeLayer.setLoadingDeliveryFilter(deliveryFilterInfo);
      this.holeLayer.setLoadingQualityFilter(null);
      this.holeLayer.setLoadingDateFilter(null);
    } else if (this.holeLayer) {
      this.holeLayer.setLoadingQualityFilter(null);
      this.holeLayer.setLoadingDateFilter(null);
      this.holeLayer.setLoadingDeliveryFilter(null);
    }
  }

  onCheckboxChange(event: Event, date: string) {
    const checkbox = event.target as HTMLInputElement;
    if (checkbox.checked) {
      this.dateSelected.update(d => [...d, date]);
    } else {
      this.dateSelected.update(d => d.filter(x => x !== date));
    }

    if (this.selectedType() === FilterType.Dates) {
      this.updateQualityFilter();
      this.RefreshHoles();
    }
  }

  onDeliveryCheckboxChange(event: Event, deliveryKey: string) {
    const checkbox = event.target as HTMLInputElement;
    if (checkbox.checked) {
      this.deliverySelected.update(d => [...d, deliveryKey]);
    } else {
      this.deliverySelected.update(d => d.filter(x => x !== deliveryKey));
    }

    if (this.selectedType() === FilterType.Delivery) {
      this.updateQualityFilter();
      this.RefreshHoles();
    }
  }

  onSelectAllChange(event: Event): void {
    const checkbox = event.target as HTMLInputElement;

    switch (this.selectedType()) {
      case FilterType.Delivery:
        if (checkbox.checked) {
          this.deliverySelected.set(
            this.uniqueDeliveries().map(d => `${d.operatorName}_${d.deliveryDate.toISOString()}`)
          );
        } else {
          this.deliverySelected.set([]);
        }
        this.updateQualityFilter();
        this.RefreshHoles();
        break;

      case FilterType.Dates:
        if (checkbox.checked) {
          this.dateSelected.set([...this.dates()]);
        } else {
          this.dateSelected.set([]);
        }
        this.updateQualityFilter();
        this.RefreshHoles();
        break;
    }
  }

  getDateColor(date: string): string {
    return HoleLayer.getColorForKey(date);
  }

  getDateColorForHole(holeName: string): string | null {
    const holeData = this.holeDataList().find((h) => h.holeName === holeName);
    if (!holeData) return null;

    if (!this.dates().includes(holeData.date)) return null;

    return HoleLayer.getColorForKey(holeData.date);
  }

  getDeliveryColor(deliveryKey: string): string {
    return HoleLayer.getColorForKey(deliveryKey);
  }

  private createHoleDataList(blast: Blast): HoleData[] {
    return blast.BlastLoading.map((hole) => ({
      holeName: hole.name || "Unknown",
      totalBulk: (hole.plannedTotalBulk > 0 && hole.measuredTotalBulk > 0)
        ? Math.abs(hole.plannedTotalBulk - hole.measuredTotalBulk) : null,
      steaming: (hole.plannedSteaming > 0 && hole.measuredSteaming > 0)
        ? Math.abs(hole.plannedSteaming - hole.measuredSteaming) : null,
      linearQt: (hole.plannedLinearQt > 0 && hole.measuredLinearQt > 0)
        ? Math.abs(hole.plannedLinearQt - hole.measuredLinearQt) : null,
      date: hole.loadingDate || "",
    }));
  }

  private createDeliveryList(blast: Blast): DeliveryData[] {
    const deliveries: DeliveryData[] = [];

    blast.BlastLoading.forEach((hole) => {
      if (!hole.deliveryDate || !hole.operatorNames || hole.operatorNames.length === 0) return;

      deliveries.push({
        holeName: hole.name,
        deliveryDate: new Date(hole.deliveryDate),
        operatorName: hole.operatorNames[0],
        deliveryId: hole.holeId,
      });
    });

    deliveries.sort((a, b) => {
      const diff = a.deliveryDate.getTime() - b.deliveryDate.getTime();
      if (diff !== 0) return diff;
      return a.holeName.localeCompare(b.holeName);
    });

    return deliveries;
  }

  private computeUniqueDeliveries(deliveries: DeliveryData[]): DeliveryData[] {
    const unique: DeliveryData[] = [];
    const seenKeys = new Set<string>();
    deliveries.forEach(d => {
      const key = `${d.operatorName}_${d.deliveryDate.toISOString()}`;
      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        unique.push(d);
      }
    });
    return unique;
  }

  private getDates(blast: Blast): string[] {
    return Array.from(
      new Set(
        blast.BlastLoading
          .map(h => h.loadingDate)
          .filter((date): date is string => date != null)
      )
    ).sort((a, b) => this.parseDate(a).getTime() - this.parseDate(b).getTime());
  }

  private parseDate(dateString: string): Date {
    const [day, month, year] = dateString.split("/").map(Number);
    return new Date(year, month - 1, day);
  }

  formatDate(date: Date, includeTime = false): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    let formatted = `${day}/${month}/${year}`;

    if (includeTime) {
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      formatted += ` ${hours}:${minutes}`;
    }

    return formatted;
  }

  trackByDelivery(index: number, delivery: DeliveryData): string {
    return `${delivery.operatorName}_${delivery.deliveryDate.toISOString()}`;
  }

  getTextQualityType(
    type: QualityTypeColor,
    holeType: LoadingHoleTypeQuality
  ): string {
    const qualityConfig = this.getQualityConfig(holeType);
    const qualityValue = this.getQualityValue(type, qualityConfig);
    return ` ${qualityValue}`;
  }

  private getQualityConfig(holeType: LoadingHoleTypeQuality) {
    const qd = this.qualityData();
    const configMap = {
      [LoadingHoleTypeQuality.Bulk]: {
        good: getThreshold(qd, 'BULK')?.asDesignedMax,
        bad: getThreshold(qd, 'BULK')?.inToleranceMax,
      },
      [LoadingHoleTypeQuality.StreamingFinal]: {
        good: getThreshold(qd, 'STEMMING')?.asDesignedMax,
        bad: getThreshold(qd, 'STEMMING')?.inToleranceMax,
      },
      [LoadingHoleTypeQuality.LinearQuantity]: {
        good: getThreshold(qd, 'LINEAR_QUANTITY')?.asDesignedMax,
        bad: getThreshold(qd, 'LINEAR_QUANTITY')?.inToleranceMax,
      },
    };

    return configMap[holeType] || { good: 0, bad: 0 };
  }

  private getQualityValue(
    type: QualityTypeColor,
    config: { good: number; bad: number }
  ): string {
    switch (type) {
      case QualityTypeColor.Good:
        return `<${config.good}`;
      case QualityTypeColor.Average:
        return `${config.good} - ${config.bad}`;
      case QualityTypeColor.Bad:
        return `>${config.bad}`;
      default:
        return "Unknown";
    }
  }

  formatOperatorName(operatorName: string): { firstLine: string; secondLine: string } {
    if (!operatorName || operatorName.length <= 16) {
      return { firstLine: operatorName || '', secondLine: '' };
    }

    const words = operatorName.split(' ');
    if (words.length === 1) {
      return {
        firstLine: operatorName.substring(0, 16),
        secondLine: operatorName.substring(16),
      };
    }

    let firstLine = '';
    let secondLine = '';
    let currentLength = 0;

    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      const potentialLength = currentLength + (currentLength > 0 ? 1 : 0) + word.length;

      if (potentialLength <= 16 || firstLine === '') {
        firstLine += (firstLine ? ' ' : '') + word;
        currentLength = potentialLength;
      } else {
        secondLine = words.slice(i).join(' ');
        break;
      }
    }

    return { firstLine, secondLine };
  }

  private clearMapLayers() {
    if (!this.map) return;

    if (this.holeLayer) {
      this.holeLayer.hideAllHoles();
      this.holeLayer.clear();
    }

    const layers = this.map.getLayers().getArray().slice();
    layers.forEach(layer => {
      if (layer.get('name') !== 'graticule' &&
          layer !== this.graticule &&
          layer.get('type') !== 'base') {
        this.map.removeLayer(layer);
      }
    });

    if (this.blastPolygoneLayer) {
      this.blastPolygoneLayer = null;
    }
    if (this.holeLayer) {
      this.holeLayer = null;
    }

    this.map.renderSync();
  }
}
